import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, ChevronRight, Settings, LogOut, UserPlus, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { ModerationNotice } from '../components/ModerationNotice';
import { useSilentRefresh } from '../lib/useSilentRefresh';
import { Avatar } from '../components/Avatar';

export const Profile = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, signOut, refreshUser } = useAuth();
  const leaving = useRef(false);

  useEffect(() => {
    if (isLoading || leaving.current) return;
    if (!isAuthenticated) navigate('/signin');
  }, [isLoading, isAuthenticated, navigate]);

  // Keeps the connection count current when an invitee joins, without a manual refresh.
  useSilentRefresh(refreshUser, !isLoading && isAuthenticated);

  const handleSignOut = () => {
    // Signing out must land on the signed-out welcome screen, not the guard above.
    leaving.current = true;
    signOut();
    navigate('/', { replace: true });
  };

  if (isLoading || !user) {
    return (
      <div className="page" data-testid="profile-page">
        <div className="loading-state"><Loader2 className="loading-spinner" /></div>
      </div>
    );
  }

  const count = user.connectionCount ?? 0;

  return (
    <div className="page" data-testid="profile-page">
      <header className="page-header">
        <h1 className="page-title">Profile</h1>
      </header>

      <section className="identity-block">
        <Avatar
          userId={user.id}
          name={user.name}
          photoUrl={user.photoUrl}
          className="identity-avatar"
          testId="profile-avatar"
        />
        <h2 className="identity-name" data-testid="profile-name">{user.name}</h2>
        <button
          className="identity-connections"
          onClick={() => navigate('/connections')}
          data-testid="profile-connections-link"
        >
          <span data-testid="profile-connection-count">
            {count} {count === 1 ? 'connection' : 'connections'}
          </span>
          <ChevronRight className="identity-connections-icon" />
        </button>
      </section>

      <button
        className="invite-cta"
        onClick={() => navigate('/invite-people')}
        data-testid="profile-invite-cta"
      >
        <UserPlus className="invite-cta-icon" />
        <span className="invite-cta-label">Connect with someone you trust</span>
        <ChevronRight className="invite-cta-chevron" />
      </button>

      {/* Stays on Profile: it is the only place a member is told their content
          was removed from Explore. Renders nothing when there is no notice. */}
      <ModerationNotice />

      <section className="profile-links">
        <button className="profile-link" onClick={() => navigate('/connections')} data-testid="profile-connections-row">
          <Users className="profile-link-icon" />
          <span className="profile-link-label">Connections</span>
          <ChevronRight className="profile-link-chevron" />
        </button>
        <button className="profile-link" onClick={() => navigate('/settings')} data-testid="profile-settings">
          <Settings className="profile-link-icon" />
          <span className="profile-link-label">Account settings</span>
          <ChevronRight className="profile-link-chevron" />
        </button>
        <button className="profile-link danger" onClick={handleSignOut} data-testid="signout-btn">
          <LogOut className="profile-link-icon" />
          <span className="profile-link-label">Sign out</span>
        </button>
      </section>
    </div>
  );
};
